var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React, { Component } from 'react';
import { Breakpoint, ButtonTheme } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, color, element, eventListener, expression, object, oneOf, oneOfType, record, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { enumValues, getBreakpoint, isEmptyArrayOrObject, isEqualShallow, isFunction, isMobile, noop } from '@philips-internal/oswald-ui-utils';
import { ActionStyle } from '../action/action-constants.js';
import { HeadingContext } from '../heading/components/heading-context/heading-context.js';
import { NotFound } from '../not-found/not-found.js';
import { TimelineEvents } from './components/timeline-events.js';
import { TimelineGroup } from './components/timeline-group.js';
import { TimelineHeader } from './components/timeline-header.js';
import { TimelineSelector } from './components/timeline-selector.js';
import { TimelineTimestamp } from './components/timeline-timestamp.js';
import { TimelineAPI } from './timeline-api.js';
import { TimelineInterval } from './timeline-constants.js';
import { getGroupedByTime, getGroupedData, getIntervals, getIntervalsValue, getTimelineWidth } from './timeline-utils.js';
import './timeline.css';
const INTERVAL_EXPRESSION = 'tws.timeline-interval';
const INTERVAL_EXPRESSION_WITH_VALUE = 'tws.timeline-interval-with-value';
const OTHERS_EXPRESSION = 'tws.timeline-others';
let Timeline = class Timeline extends Component {
    constructor() {
        super(...arguments);
        this.popClassName = noop;
        this.state = {
            interval: TimelineInterval.ONE_HOUR,
            intervals: [],
        };
        this.onIntervalChange = (interval) => {
            const { data } = this.props;
            const intervals = getIntervals(data, interval);
            this.setState({ interval, intervals });
        };
        this.onDynamicContentRender = (event) => {
            const { onDynamicContentRender } = this.props;
            if (isFunction(onDynamicContentRender)) {
                onDynamicContentRender(event);
            }
        };
    }
    componentDidMount() {
        const { initialInterval, intervalOption } = this.props;
        if (isMobile() && intervalOption) {
            this.addContextualBarAction();
        }
        if (initialInterval !== TimelineInterval.ONE_HOUR) {
            this.setState({ interval: initialInterval });
        }
        if (this.context) {
            this.popClassName = this.context.pushCustomClassName('c-timeline__contextual-bar--no-padding');
        }
    }
    componentDidUpdate(prevProps) {
        if (isEqualShallow(this.props, prevProps)) {
            return;
        }
        this.updateIntervals();
    }
    componentWillUnmount() {
        this.popClassName();
    }
    addContextualBarAction() {
        const options = getIntervalsValue().map(interval => ({
            enabled: true,
            id: interval,
            onClick: () => this.onIntervalChange(interval),
            style: ActionStyle.TEXT,
            text: interval,
        }));
        this.context?.addOrUpdateAction({
            buttonTheme: ButtonTheme.OUTLINE,
            enabled: true,
            id: 'timeline-intervals',
            onClick: noop,
            options,
            style: ActionStyle.DROPDOWN,
            text: INTERVAL_EXPRESSION,
        });
    }
    updateIntervals() {
        const { data } = this.props;
        const { interval } = this.state;
        if (!isEmptyArrayOrObject(data)) {
            const intervals = getIntervals(data, interval);
            this.setState({ intervals });
        }
    }
    isMobile() {
        const breakpoint = getBreakpoint();
        return breakpoint === Breakpoint.XSM;
    }
    renderMobile() {
        const { data } = this.props;
        const { interval } = this.state;
        const grouped = getGroupedByTime(data, interval);
        if (!grouped) {
            return null;
        }
        return this.renderEventGroups(grouped);
    }
    renderDesktop() {
        const { data, groupBy, onClick, onContextMenuClick, onDoubleClick, superscription, superscriptionEntry } = this.props;
        const { interval, intervals } = this.state;
        if (groupBy) {
            const groupedData = getGroupedData(data, groupBy);
            return (React.createElement("div", { className: "c-timeline u-scrollbar" },
                this.renderHeader(),
                !groupedData ? null : this.renderEventGroups(groupedData),
                React.createElement(TimelineTimestamp, { interval: interval, intervals: intervals })));
        }
        return (React.createElement("div", { className: "c-timeline u-scrollbar" },
            this.renderHeader(),
            React.createElement(TimelineEvents, { data: data, interval: interval, intervals: intervals, onClick: onClick, onContextMenuClick: onContextMenuClick, onDoubleClick: onDoubleClick, onDynamicContentRender: this.onDynamicContentRender, superscription: superscription, superscriptionEntry: String(superscriptionEntry) }),
            React.createElement(TimelineTimestamp, { interval: interval, intervals: intervals })));
    }
    renderHeader() {
        const { intervals } = this.state;
        return (React.createElement("div", { className: "c-timeline-top", style: { width: `${getTimelineWidth(intervals.length)}rem` } },
            this.renderSelectors(),
            React.createElement(TimelineHeader, { intervals: intervals })));
    }
    renderSelectors() {
        const { interval } = this.state;
        const intervalTitle = MessageService.translate(INTERVAL_EXPRESSION_WITH_VALUE, { interval });
        return (React.createElement("div", { className: "c-timeline-selectors" },
            React.createElement(TimelineSelector, { items: getIntervalsValue(), onChange: this.onIntervalChange, title: intervalTitle })));
    }
    renderEventGroups(data) {
        const { groupBy, onClick, onCollapse, onContextMenuClick, onDoubleClick, superscription, superscriptionEntry, ungroupedTitle } = this.props;
        const { interval, intervals } = this.state;
        return Object.entries(data).map(([groupKey, item]) => {
            return (React.createElement(TimelineGroup, { data: item, groupBy: groupBy, groupKey: groupKey, groupedByTime: this.isMobile(), interval: interval, intervals: intervals, key: groupKey, onClick: onClick, onCollapse: onCollapse, onContextMenuClick: onContextMenuClick, onDoubleClick: onDoubleClick, onDynamicContentRender: this.onDynamicContentRender, superscription: superscription, superscriptionEntry: String(superscriptionEntry), ungroupedTitle: ungroupedTitle }));
        });
    }
    render() {
        const { data } = this.props;
        if (!data || isEmptyArrayOrObject(data) || !Array.isArray(data)) {
            return React.createElement(NotFound, null);
        }
        return this.isMobile()
            ? this.renderMobile()
            : this.renderDesktop();
    }
};
Timeline.contextType = HeadingContext;
Timeline.propTypes = {
    className: string,
    data: arrayOf(object),
    groupBy: string.group('group'),
    initialInterval: oneOf(enumValues(TimelineInterval)),
    intervalOption: bool,
    onClick: eventListener,
    onCollapse: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onDynamicContentRender: eventListener,
    superscription: record({ color: color, icon: oneOfType([string, element]), label: string, value: string }),
    superscriptionEntry: string,
    ungroupedTitle: expression,
    uuid: string,
};
Timeline.defaultProps = {
    className: '',
    groupBy: '',
    initialInterval: TimelineInterval.ONE_HOUR,
    intervalOption: true,
    superscriptionEntry: 'status',
    ungroupedTitle: OTHERS_EXPRESSION,
};
Timeline = __decorate([
    AwesomeComponent({ api: TimelineAPI, type: ComponentType.LIST })
], Timeline);
export { Timeline, };
//# sourceMappingURL=timeline.js.map